import React, { MutableRefObject } from 'react';
import { Container, Stack, Typography, Box, Grid } from '@mui/material';
import { Icon, IconType, MuiTab, MuiSelector } from '@ace/ace-ui-web';
import { Shift, ShiftAction } from '../shiftcard/ShiftCard';
import ShiftCard from '../shiftcard';

interface Tab {
    label: string;
    content: string;
}

interface FilterOption {
    label: string;
    value: string;
}

interface ShiftPageContainerProps {
    title: string;
    subtitle: string;
    tabs: Tab[];
    selectedTab: number;
    onTabChange: (tabIndex: number) => void;
    shifts: Shift[];
    loading: boolean;
    noShiftsMessage?: string;
    filterLabel?: string;
    filterOptions?: FilterOption[];
    filterValue?: string;
    onFilterChange?: (value: string) => void;
    filterDisabled?: boolean;
    onShiftAction?: (shift: Shift, action: ShiftAction, rejectionNotes?: string) => void;
    isApproveReject?: boolean;
    readOnly?: boolean;
    highlightedShiftId?: string | null;
    shiftRefs?: MutableRefObject<Map<string, HTMLDivElement>>;
}

// Highlight animation constants
const HIGHLIGHT_BORDER_WIDTH = '3px';
const HIGHLIGHT_BORDER_RADIUS = '12px';
const HIGHLIGHT_COLOR = '#FF9800';
const HIGHLIGHT_BOX_SHADOW_BASE = '0 8px 24px rgba(255, 152, 0, 0.3)';
const HIGHLIGHT_BOX_SHADOW_PULSE = '0 12px 32px rgba(255, 152, 0, 0.5)';

const ShiftPageContainer: React.FC<ShiftPageContainerProps> = ({
    title,
    subtitle,
    tabs,
    selectedTab,
    onTabChange,
    shifts,
    loading,
    noShiftsMessage = "No shifts available",
    filterLabel,
    filterOptions,
    filterValue,
    onFilterChange,
    filterDisabled = false,
    onShiftAction,
    isApproveReject = false,
    readOnly = false,
    highlightedShiftId = null,
    shiftRefs,
}) => {
    const hasFilter = filterLabel && filterOptions && onFilterChange;
    const hasShifts = shifts.length > 0;
    const noShiftsReturned = !hasShifts && !loading;

    const setShiftRef = (id: string, element: HTMLDivElement | null) => {
        if (shiftRefs && element) {
            shiftRefs.current.set(id, element);
        }
    };

    return (
        <Container
            maxWidth={false}
            sx={{
                py: { xs: 2.5, md: 3, lg: 3.5, xl: 4 },
                px: { xs: 1, sm: 2, md: 3.5, lg: 4, xl: 5 }
            }}
        >
            <Stack spacing={2.5}>
                {/* Page Header */}
                <Stack spacing={0.5}>
                    <Typography variant="h3" component="h1" className="page-title">
                        {title}
                    </Typography>
                    <Typography variant="subtitle1" className="page-subtitle">
                        {subtitle}
                    </Typography>
                </Stack>

                {/* Tabs and Filter */}
                <Stack spacing={2}>
                    <Grid
                        direction="row"
                        container
                        spacing={0.5}
                        alignItems="flex-start"
                    >
                        <Grid size={hasFilter ? { xs: 12, md: 6 } : 12} sx={{ order: { xs: 0, md: 0 } }}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: { xs: 'center', md: 'flex-start' },
                            }}>
                                <Box sx={{
                                    width: 'fit-content',
                                    '& [role="tabpanel"]': { display: 'none' }
                                }}>
                                    <MuiTab
                                        fontSize="default"
                                        fontWeight="default"
                                        variant="button"
                                        tabs={tabs}
                                        value={selectedTab}
                                        onChange={onTabChange}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                        {hasFilter && (
                            <Grid size={{ xs: 12, md: 6 }} sx={{ order: { xs: 1, md: 1 } }}>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    justifyContent={{ xs: 'center', md: 'flex-end' }}
                                >
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {filterLabel}
                                    </Typography>
                                    <MuiSelector
                                        value={filterValue}
                                        options={filterOptions}
                                        onChange={onFilterChange}
                                        disabled={filterDisabled}
                                        placeholder="Select..."
                                    />
                                </Stack>
                            </Grid>
                        )}
                    </Grid>

                    {/* Content Area */}
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Typography variant="body2" sx={{ color: "#666" }}>
                                Loading shifts...
                            </Typography>
                        </Box>
                    )}

                    {!loading && noShiftsReturned && (
                        <Stack
                            spacing={1.5}
                            alignItems="center"
                            justifyContent="center"
                            sx={{ py: 6, textAlign: 'center' }}
                        >
                            <Icon iconType={IconType.alertCircle} style={{ color: "var(--ace-color-alert)" }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
                                {noShiftsMessage}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#666" }}>
                                Try viewing another tab
                            </Typography>
                        </Stack>
                    )}

                    {!loading && hasShifts && (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: 'repeat(1, 1fr)',
                                    lg: 'repeat(2, 1fr)'
                                },
                                gap: { xs: 2, md: 2.5 }
                            }}
                        >
                            {shifts.map((shift) => {
                                const isHighlighted = highlightedShiftId === shift.id;

                                return (
                                    <Box
                                        key={shift.id}
                                        ref={(el: HTMLDivElement | null) => {
                                            if (el) {
                                                setShiftRef(shift.id, el);
                                            }
                                        }}
                                        sx={{
                                            ...(isHighlighted && {
                                                animation: 'highlight-pulse 2s ease-in-out 3',
                                                border: `${HIGHLIGHT_BORDER_WIDTH} solid ${HIGHLIGHT_COLOR}`,
                                                borderRadius: HIGHLIGHT_BORDER_RADIUS,
                                                boxShadow: HIGHLIGHT_BOX_SHADOW_BASE,
                                                '@keyframes highlight-pulse': {
                                                    '0%': {
                                                        boxShadow: HIGHLIGHT_BOX_SHADOW_BASE,
                                                    },
                                                    '50%': {
                                                        boxShadow: HIGHLIGHT_BOX_SHADOW_PULSE,
                                                    },
                                                    '100%': {
                                                        boxShadow: HIGHLIGHT_BOX_SHADOW_BASE,
                                                    },
                                                },
                                            }),
                                        }}
                                    >
                                        <ShiftCard
                                            shift={shift}
                                            onHandleConfirm={onShiftAction}
                                            isApproveReject={isApproveReject}
                                            readOnly={readOnly}
                                        />
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Stack>
            </Stack>
        </Container>
    );
};

export default ShiftPageContainer;